<template>
  <div class="organization-switcher relative">
    <button
      @click="isOpen = !isOpen"
      class="flex items-center space-x-2 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
      :class="{ 'bg-gray-100': isOpen }"
    >
      <div class="flex items-center space-x-2">
        <div v-if="currentOrganization?.logo" class="w-6 h-6">
          <img
            :src="currentOrganization.logo"
            :alt="currentOrganization.name"
            class="w-full h-full rounded-full object-cover"
          />
        </div>
        <div
          v-else
          class="w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-sm font-medium"
        >
          {{ currentOrganization?.name?.charAt(0) || '?' }}
        </div>
        <span class="text-sm font-medium truncate max-w-[150px]">
          {{ currentOrganization?.name || 'Selecione uma organização' }}
        </span>
      </div>
      <i class="pi pi-chevron-down text-sm" :class="{ 'transform rotate-180': isOpen }"></i>
    </button>

    <!-- Dropdown -->
    <div
      v-if="isOpen"
      class="absolute left-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-100 py-2 z-50"
    >
      <!-- Search -->
      <div class="px-3 pb-2">
        <div class="relative">
          <span class="absolute inset-y-0 left-0 pl-3 flex items-center text-gray-400">
            <i class="pi pi-search text-sm"></i>
          </span>
          <input
            v-model="searchQuery"
            type="text"
            placeholder="Buscar organização..."
            class="w-full pl-10 pr-4 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:border-primary"
          />
        </div>
      </div>

      <!-- Organizations List -->
      <div class="max-h-64 overflow-y-auto">
        <template v-if="filteredOrganizations.length">
          <button
            v-for="org in filteredOrganizations"
            :key="org.id"
            @click="switchOrganization(org)"
            class="w-full px-3 py-2 flex items-center space-x-3 hover:bg-gray-50 transition-colors"
            :class="{ 'bg-primary/5': org.id === currentOrganization?.id }"
          >
            <div v-if="org.logo" class="w-8 h-8">
              <img
                :src="org.logo"
                :alt="org.name"
                class="w-full h-full rounded-full object-cover"
              />
            </div>
            <div
              v-else
              class="w-8 h-8 rounded-full bg-primary/10 text-primary flex items-center justify-center font-medium"
            >
              {{ org.name.charAt(0) }}
            </div>
            <div class="flex-1 text-left">
              <div class="text-sm font-medium">{{ org.name }}</div>
              <div class="text-xs text-gray-500">
                {{ org.role === 'admin' ? 'Administrador' : 'Usuário' }}
              </div>
            </div>
            <i
              v-if="org.id === currentOrganization?.id"
              class="pi pi-check text-primary"
            ></i>
          </button>
        </template>
        <div
          v-else
          class="px-3 py-2 text-sm text-gray-500 text-center"
        >
          Nenhuma organização encontrada
        </div>
      </div>

      <!-- Footer -->
      <div v-if="isAdmin" class="mt-2 pt-2 border-t border-gray-100 px-3">
        <button
          @click="goToOrganizations"
          class="w-full text-left px-3 py-2 text-sm text-primary hover:bg-primary/5 rounded-lg transition-colors"
        >
          <i class="pi pi-cog mr-2"></i>
          Gerenciar Organizações
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useOrganizationStore } from '@/stores/organization'
import { useCurrentOrganizationStore } from '@/stores/currentOrganization'
import { useAuthStore } from '@/stores/auth'

const router = useRouter()
const route = useRoute()
const organizationStore = useOrganizationStore()
const currentOrganizationStore = useCurrentOrganizationStore()
const authStore = useAuthStore()

const isOpen = ref(false)
const searchQuery = ref('')

// Computed property para filtrar organizações
const filteredOrganizations = computed(() => {
  const organizations = organizationStore.organizations
  const userOrgs = authStore.userOrganizations

  // Filtra apenas as organizações que o usuário tem acesso
  const allowedOrganizations = organizations.filter(org => 
    userOrgs.some(userOrg => userOrg.organizationId === org.id)
  )

  // Adiciona a role do usuário em cada organização
  const orgsWithRole = allowedOrganizations.map(org => {
    const userOrg = userOrgs.find(uo => uo.organizationId === org.id)
    return {
      ...org,
      role: userOrg?.role || 'user'
    }
  })

  // Aplica o filtro de busca
  return orgsWithRole.filter(org =>
    org.name.toLowerCase().includes(searchQuery.value.toLowerCase())
  )
})

// Computed property para verificar se é admin
const isAdmin = computed(() => {
  const currentOrg = currentOrganizationStore.currentOrganization
  if (!currentOrg) return false
  
  const userOrg = authStore.userOrganizations.find(
    uo => uo.organizationId === currentOrg.id
  )
  return userOrg?.role === 'admin'
})

// Computed property para a organização atual
const currentOrganization = computed(() => {
  const org = currentOrganizationStore.currentOrganization
  if (!org) return null

  const userOrg = authStore.userOrganizations.find(
    uo => uo.organizationId === org.id
  )
  return {
    ...org,
    role: userOrg?.role || 'user'
  }
})

// Trocar de organização
const switchOrganization = async (org: any) => {
  try {
    // Atualiza a organização atual
    await currentOrganizationStore.setCurrentOrganization(org)
    isOpen.value = false

    // Salva a nova organização no localStorage
    localStorage.setItem('currentOrganizationId', org.id)

    // Força um refresh da rota atual
    const currentRoute = router.currentRoute.value
    await router.replace({ name: 'loading' })
    await router.replace(currentRoute)
  } catch (error) {
    console.error('Erro ao trocar de organização:', error)
  }
}

const goToOrganizations = () => {
  router.push('/organizations')
  isOpen.value = false
}

// Inicializa a organização atual
onMounted(async () => {
  try {
    // Primeiro busca as organizações
    await organizationStore.fetchOrganizations()
    
    // Busca as organizações que o usuário tem acesso
    const userOrgs = authStore.userOrganizations
    const allowedOrganizations = organizationStore.organizations.filter(org => 
      userOrgs.some(userOrg => userOrg.organizationId === org.id)
    )
    
    // Se temos organizações permitidas mas nenhuma selecionada, seleciona a primeira
    if (allowedOrganizations.length > 0 && !currentOrganizationStore.currentOrganization) {
      await currentOrganizationStore.setCurrentOrganization(allowedOrganizations[0])
    }
  } catch (error) {
    console.error('Error initializing organization switcher:', error)
  }
})

// Click outside handler
const handleClickOutside = (event: MouseEvent) => {
  const target = event.target as HTMLElement
  if (!target.closest('.organization-switcher')) {
    isOpen.value = false
  }
}

// Escape key handler
const handleEscKey = (event: KeyboardEvent) => {
  if (event.key === 'Escape') {
    isOpen.value = false
  }
}

// Add event listeners
onMounted(() => {
  document.addEventListener('click', handleClickOutside)
  document.addEventListener('keydown', handleEscKey)
})

// Remove event listeners
onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
  document.removeEventListener('keydown', handleEscKey)
})
</script>
